import classNames from 'classnames'
import React, {
  ChangeEventHandler,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { PortableTextBodyElement } from '../../../utilities/blockContent'
import { isNullOrWhiteSpace } from '../../../utilities/string'
import { Button, ButtonTheme } from '../../Button'
import { Checkbox } from '../../Forms/Checkbox'
import { ExternalIconSize } from '../../Link'
import { Paragraph } from '../../Paragraph'
import { RichTextArea } from '../../PortableText/RichTextArea'
import { BasicBlockSerializer } from '../../PortableText/serializers/blocks/BasicBlockSerializer'

export interface AnnexOneProps {
  acceptTermsButtonLabel?: string
  acceptTermsCheckboxLabel?: string
  acceptTermsText?: string
  body?: PortableTextBodyElement[]
  declineTermsButtonLabel?: string
  declineTermsUrl?: string
  onAccept?: () => void
  title?: string
}

export const AnnexOne = ({
  acceptTermsButtonLabel,
  acceptTermsCheckboxLabel,
  acceptTermsText,
  body,
  declineTermsButtonLabel,
  declineTermsUrl,
  onAccept,
  title,
}: AnnexOneProps) => {
  const [checked, setChecked] = useState(isNullOrWhiteSpace(acceptTermsCheckboxLabel))

  const renderDeclineButton =
    !isNullOrWhiteSpace(declineTermsButtonLabel) && !isNullOrWhiteSpace(declineTermsUrl)

  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView()
    }
  }, [])

  const onAcceptTermsChange = useCallback<ChangeEventHandler<HTMLInputElement>>(e => {
    setChecked(e.currentTarget.checked)
  }, [])

  return (
    <div>
      {!isNullOrWhiteSpace(title) && (
        <h1 className="mb-30 font-h2 text-h2 text-secondary-dark" ref={scrollRef}>
          {title}
        </h1>
      )}

      <div className="flex flex-col gap-45">
        {body && (
          <div
            className={classNames(
              'p-30 rounded-15 border border-neutral-lighter-alt',
              'font-t text-t-300 leading-[145%] text-secondary-darkest'
            )}
          >
            <RichTextArea
              components={{
                block: BasicBlockSerializer,
              }}
              value={body}
            />
          </div>
        )}

        {!isNullOrWhiteSpace(acceptTermsText) && (
          <Paragraph className="font-body text-body text-secondary-darkest">
            {acceptTermsText}
          </Paragraph>
        )}

        {!isNullOrWhiteSpace(acceptTermsCheckboxLabel) && (
          <Checkbox
            additionalClassName="self-start"
            label={acceptTermsCheckboxLabel}
            name="accept-terms"
            onChange={onAcceptTermsChange}
            value="accept-terms"
            variant="button"
          />
        )}

        {(!isNullOrWhiteSpace(acceptTermsButtonLabel) || renderDeclineButton) && (
          <div className="flex flex-col items-start gap-15 tb:flex-row">
            {!isNullOrWhiteSpace(acceptTermsButtonLabel) && (
              <Button
                disabled={!checked}
                id="accept-disclaimer"
                onClick={onAccept}
                theme={ButtonTheme.PrimaryInverted}
              >
                {acceptTermsButtonLabel}
              </Button>
            )}

            {renderDeclineButton && (
              <Button
                externalIconSize={ExternalIconSize.None}
                isExternalUrl
                theme={ButtonTheme.Primary}
                url={declineTermsUrl}
              >
                {declineTermsButtonLabel}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
